import { Pipe, PipeTransform } from '@angular/core';
import { Models } from '@ibexfusion/models';

import { ContentService } from '../../services/content/content.service';
import { HelperService } from '../../services/helper/helper.service';

@Pipe({
  name: 'contentOpeningHours'
})
export class OpeningHoursPipe implements PipeTransform {
  weekDay = { 0: 'sunday', 1: 'monday', 2: 'tuesday', 3: 'wednesday', 4: 'thursday', 5: 'friday', 6: 'saturday' };
  private today = new Date();

  constructor(private contentService: ContentService) {
  }

  transform(entry: Models.ContentEntry): any {
    if (!entry || (!entry.files && !entry.title && (!entry.detail || Object.keys(entry.detail).length === 0))) {
      return null;
    }

    if (!this.contentService.isValid(entry)) {
      return null;
    }

    const openingHoursEntry: any = new Models.ContentOpeningHours();
    if (entry?.related?.entries?.length > 0) {
      const entries: Models.ContentOpeningHours[] = entry.related.entries.map(e => this.transform(this.contentService.getEntry(e.id)))
        .filter(Boolean);
      return entries.length === 0 ? null : entries;
    }

    let data = entry.detail.openingHours;
    let showInWidget = entry && entry.detail && entry.detail.showInWidget ? entry.detail.showInWidget : null;
    let raw;
    if (entry.detail.raw) {
      try {
        raw = JSON.parse(entry.detail.raw);
        showInWidget = !!raw.showInWidget ? raw.showInWidget : showInWidget;
        data = !data ? raw.openingHours : data;
      } catch (err) {}
    }
    if (!data && (!entry?.detail?.openingInfo && !raw?.openingInfo)) {
      return null;
    }

    if (!!data) {
      openingHoursEntry.openingHours = {
        exceptionDays: data.exceptionDays ? data.exceptionDays : [], regularOpeningHours: data?.regularOpeningHours
      };

      for (let i = 0; i < openingHoursEntry.openingHours.exceptionDays.length; i++) {
        for (let t = 0; t < openingHoursEntry.openingHours.exceptionDays[i].times.length; t++) {
          if (openingHoursEntry.openingHours.exceptionDays[i].opened || openingHoursEntry.openingHours.exceptionDays[i].open) {
            if (!openingHoursEntry.openingHours.exceptionDays[i].openFullDay) {
              const time = openingHoursEntry.openingHours.exceptionDays[i].times[t];
              time.startTime = this.fixTime(time.startTime);
              time.endTime = this.fixTime(time.endTime);
              if (!time.startTime.length && !time.Time.length) {
                openingHoursEntry.openingHours.exceptionDays[i].times.splice(t, 1);
              } else {
                openingHoursEntry.openingHours.exceptionDays[i].times[t] = time;
              }
            }
          }
        }
      }
      if (openingHoursEntry?.openingHours?.regularOpeningHours) {
        for (let key of Object.keys(openingHoursEntry.openingHours.regularOpeningHours)) {
          for (let t = 0; t < openingHoursEntry?.openingHours?.regularOpeningHours[key]?.times?.length || 0; t++) {
            if (openingHoursEntry.openingHours.regularOpeningHours[key].opened || openingHoursEntry.openingHours.regularOpeningHours[key].open) {
              if (!openingHoursEntry.openingHours.regularOpeningHours[key].openFullDay) {
                const time = openingHoursEntry.openingHours.regularOpeningHours[key].times[t];
                time.startTime = this.fixTime(time.startTime);
                time.endTime = this.fixTime(time.endTime);
                if (!time.startTime.length && !time.endTime.length) {
                  openingHoursEntry.openingHours.regularOpeningHours[key].times.splice(t, 1);
                } else {
                  openingHoursEntry.openingHours.regularOpeningHours[key].times[t] = time;
                }
              }
            }
          }
        }
      }
    } else {
      openingHoursEntry.openingHours = HelperService.initOpeningData(entry.detail.openingInfo ? entry.detail : raw);
    }
    openingHoursEntry.populateFromJSON({ textContent: entry?.detail?.description, showInWidget });
    openingHoursEntry.title = entry.title;
    openingHoursEntry.sourceId = entry._id;
    openingHoursEntry.media = this.contentService.getMediaEntriesForEntry(entry.getId());
    openingHoursEntry.documents = this.contentService.getDocumentEntriesForEntry(entry?.getId?.() || entry?._id);
    openingHoursEntry.openingHours = HelperService.initOpeningData(entry.detail) || openingHoursEntry.openingHours;
    return this.formatData(openingHoursEntry);
  }

  fixTime(time: string): string {
    if (!time) {
      return '';
    }
    const postMeridian = !!time?.includes('pm') && !time?.startsWith('12');
    time = time.replace(/[^\d:.]/gm, '').replace(/\./, ':');
    if (time && time.indexOf(':') === -1) {
      time = this.floatToStringTime(parseFloat(time) / 100);
    }
    if (postMeridian) {
      time = this.floatToStringTime(parseFloat(time.replace(':', '')) / 100 + 12);
    }
    return time;
  }

  floatToStringTime(floatTime) {
    try {
      const tempTime = floatTime.toString().split('.');
      if (tempTime[0].length < 2) {
        tempTime[0] = '0' + tempTime[0];
      }
      if (!tempTime[1]) {
        tempTime[1] = '00';
      } else if (tempTime[1].length === 1) {
        tempTime[1] = tempTime[1] + '0';
      }
      floatTime = tempTime.join('.');
      return floatTime;
    } catch (err) {
      return floatTime;
    }
  }

  formatData(openingHoursEntry) {
    const todayDay = this.today.getDay();
    const todayHours = this.today.getHours();
    if (openingHoursEntry?.openingHours?.exceptionDays) {
      openingHoursEntry = this.checkExceptionDays(openingHoursEntry);
    }
    if (!openingHoursEntry?.openingHours?.regularOpeningHours || !openingHoursEntry?.openingHours?.regularOpeningHours[this.weekDay[todayDay]]) {
      openingHoursEntry.opened = false;
    } else {
      openingHoursEntry.opened = openingHoursEntry?.openingHours?.regularOpeningHours[this.weekDay[todayDay]].open;
    }
    if (openingHoursEntry.opened) {
      const times = openingHoursEntry?.openingHours?.regularOpeningHours[this.weekDay[todayDay]].times;
      for (let t = 0; t < times.filter(t => !!t.startTime && t.endTime).length; t++) {
        // check if time is before working hours
        const startDate = new Date() as any;
        const endDate = new Date() as any;
        startDate.setHours(+times[t].startTime.split(':')[0]);
        startDate.setMinutes(+times[t].startTime.split(':')[1]);
        startDate.setSeconds(0);
        endDate.setHours(+times[t].endTime.split(':')[0]);
        endDate.setMinutes(+times[t].endTime.split(':')[1]);
        endDate.setSeconds(59);
        if (endDate < startDate) {
          endDate.setDate(endDate.getDate() + 1);
        }
        if (t === 0 && this.today < startDate) {
          openingHoursEntry.opened = false;
          openingHoursEntry.time = times[t].startTime;
          break;
        }
        //
        if ((this.today > startDate) && (this.today < endDate)) {
          openingHoursEntry.opened = true;
          openingHoursEntry.time = times[t].endTime;
          break;
        } else {
          openingHoursEntry.opened = false;
          if (t !== times.length - 1) {
            if (+todayHours > +times[t].endTime.split(':')[0] && +todayHours < +times[t + 1].startTime.split(':')[0]) {
              openingHoursEntry.time = times[t + 1].startTime;
              break;
            }
          }
          if (t === times.length - 1) {
            openingHoursEntry = this.findNextDay(openingHoursEntry);
          }
        }
      }
    } else {
      openingHoursEntry = this.findNextDay(openingHoursEntry);
    }
    return openingHoursEntry;
  }

  findNextDay(openingHoursEntry) {
    let d = 1;
    while (d < 7) {
      const nextDay = new Date(this.today);
      nextDay.setDate(nextDay.getDate() + d);
      if (
        openingHoursEntry?.openingHours?.regularOpeningHours &&
        openingHoursEntry.openingHours.regularOpeningHours[this.weekDay[nextDay.getDay()]] &&
        openingHoursEntry.openingHours.regularOpeningHours[this.weekDay[nextDay.getDay()]].open &&
        openingHoursEntry.openingHours.regularOpeningHours[this.weekDay[nextDay.getDay()]].times &&
        openingHoursEntry.openingHours.regularOpeningHours[this.weekDay[nextDay.getDay()]].times.length
      ) {
        openingHoursEntry.day = this.weekDay[nextDay.getDay()];
        openingHoursEntry.time = openingHoursEntry.openingHours.regularOpeningHours[this.weekDay[nextDay.getDay()]].times[0].startTime;
        if (d === 1) {
          openingHoursEntry.isTomorrow = true;
        }
        break;
      }
      d++;
    }
    return openingHoursEntry;
  }

  checkExceptionDays(openingHours) {
    if (openingHours.openingHours.exceptionDays && openingHours.openingHours.exceptionDays.length) {
      for (let i = 0; i < openingHours.openingHours.exceptionDays.length; i++) {
        const date = new Date(openingHours.openingHours.exceptionDays[i].date);
        const todayDate = new Date();
        todayDate.setHours(0, 0, 0, 0);
        if (date.getTime() === todayDate.getTime()) {
          const todayDay = this.today.getDay();
          openingHours.openingHours.regularOpeningHours[this.weekDay[todayDay]].times = openingHours.openingHours.exceptionDays[i].times;
          openingHours.openingHours.regularOpeningHours[this.weekDay[todayDay]].open = openingHours.openingHours.exceptionDays[i].open;
          openingHours.exceptionDay = openingHours.openingHours.exceptionDays[i].title;
        }
      }
    }
    return openingHours;
  }

}
